/** O que mudou de sítio no friso, e a quem mostrar isso.
 *
 *  Na beta.123 o friso passou a agrupar pelo que se quer fazer e não
 *  pelo sítio onde a coisa aparece. Foi a mudança certa, mas quem já
 *  usava a aplicação ficou a procurar comandos que estavam noutro
 *  separador, sem nada que o explicasse.
 *
 *  A quem se mostra. Não a toda a gente: para quem abre a aplicação
 *  pela primeira vez, uma nota sobre um friso que nunca viu é ruído.
 *  O sinal é a última versão que este browser viu, gravada aqui a cada
 *  arranque:
 *
 *   - nunca viu nenhuma e não tem mais nada guardado: chegou agora,
 *     não se diz nada;
 *   - nunca viu nenhuma mas já tem preferências guardadas: usou a
 *     aplicação antes de passarmos a registar a versão, logo é capaz
 *     de ter visto o friso antigo;
 *   - viu uma anterior à beta.123: viu mesmo o friso antigo.
 *
 *  Uma primeira tentativa usava o separador activo guardado: se lá
 *  estivesse `cena`, o browser tinha usado o friso antigo. Não serve.
 *  Esse valor é traduzido para o nome novo logo no primeiro arranque
 *  depois da actualização, portanto a prova apaga-se a si própria
 *  antes de alguém a poder ler.
 */

/** A chave do separador activo do friso. Vive aqui, e o friso importa-a
 *  daqui: tê-la escrita em dois sítios já deu erro uma vez. */
export const TAB_STORAGE_KEY = 'vn2r.ribbon.activeTab'
export const MOVES_DISMISSED_KEY = 'vn2r.ribbon.movesSeen'
export const LAST_VERSION_KEY = 'vn2r.lastSeenVersion'

/** A versão em que o friso foi reorganizado. */
export const VERSAO_DA_MUDANCA = '0.2.0-beta.123'

export interface RibbonMove {
  comando: string
  de: string
  para: string
  porque: string
}

/** O mapeamento, na ordem por que faz sentido lê-lo: primeiro o
 *  separador que mudou de nome, depois o que saiu dele. */
export const RIBBON_MOVES: RibbonMove[] = [
  {
    comando: 'Separador Cena',
    de: 'Cena',
    para: 'Aparência',
    porque:
      '"Cena" e "Vista" queriam dizer a mesma coisa e ninguém adivinhava ' +
      'em qual estava o quê. Aparência ficou só com o modo de desenhar: ' +
      'cores, arestas, fundo, fantasma e as referências.',
  },
  {
    comando: 'Nuvem de pontos',
    de: 'Cena > Camadas',
    para: 'Ficheiro > Modelo',
    porque: 'O gesto é importar um ficheiro, como abrir um IFC.',
  },
  {
    comando: 'Vistas 2D, Viewpoints e Mapa',
    de: 'Cena',
    para: 'Vista > Vistas',
    porque: 'São vistas, e é esse o nome do separador.',
  },
  {
    comando: 'Pins BCF',
    de: 'Cena',
    para: 'Vista > Vistas',
    porque:
      'Um pin é um tópico BCF com viewpoint, não uma forma de desenhar ' +
      'o modelo. Ficou junto dos Viewpoints.',
  },
  {
    comando: 'Aparência',
    de: 'Vista > Visibilidade',
    para: 'Aparência > Desenho',
    porque: 'O botão abre o painel de pintura e tem o nome do separador.',
  },
  {
    comando: 'Origem, grelha, levels e grids IFC',
    de: 'Vista > Cena',
    para: 'Aparência > Referências',
    porque: 'São auxiliares de desenho, não comandos de câmara.',
  },
]

export interface Armazenamento {
  getItem: (chave: string) => string | null
  setItem: (chave: string, valor: string) => void
}

function armazenamento(): Armazenamento | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    // Um browser em modo privado, ou com os dados do sítio bloqueados,
    // atira só ao tocar no localStorage.
    return null
  }
}

function ler(store: Armazenamento | null, chave: string): string | null {
  try {
    return store?.getItem(chave) ?? null
  } catch {
    return null
  }
}

/** Compara duas versões nossas (`0.2.0-beta.NNN`).
 *
 *  Só precisa de ordenar entre si versões deste formato, por isso
 *  compara os números por ordem e o sufixo beta no fim. Devolve
 *  negativo se `a` for anterior a `b`. */
export function compararVersoes(a: string, b: string): number {
  const partes = (v: string) =>
    v.split(/[.-]/).map((p) => (/^\d+$/.test(p) ? Number(p) : p))
  const pa = partes(a)
  const pb = partes(b)
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const x = pa[i]
    const y = pb[i]
    if (x === undefined) return -1
    if (y === undefined) return 1
    if (x === y) continue
    if (typeof x === 'number' && typeof y === 'number') return x - y
    return String(x) < String(y) ? -1 : 1
  }
  return 0
}

/** Já usou a aplicação antes, mesmo sem versão registada? */
function temPreferenciasGuardadas(store: Armazenamento | null): boolean {
  return ler(store, TAB_STORAGE_KEY) !== null
}

export function notaJaDispensada(
  store: Armazenamento | null = armazenamento(),
): boolean {
  return ler(store, MOVES_DISMISSED_KEY) === '1'
}

export function dispensarNota(
  store: Armazenamento | null = armazenamento(),
): void {
  try {
    store?.setItem(MOVES_DISMISSED_KEY, '1')
  } catch {
    // Sem onde guardar, a nota volta a aparecer no próximo arranque.
    // É melhor isso do que rebentar ao carregar em "Percebi".
  }
}

/** Grava a versão que este browser está a ver. Chamada uma vez por
 *  arranque, DEPOIS de se decidir se a nota aparece. */
export function registarVersaoVista(
  versao: string,
  store: Armazenamento | null = armazenamento(),
): void {
  try {
    store?.setItem(LAST_VERSION_KEY, versao)
  } catch {
    /* sem onde guardar: a nota pode repetir-se, e mais nada */
  }
}

/** Este browser é capaz de ter visto o friso antigo? */
export function podeTerVistoFrisoAntigo(
  store: Armazenamento | null = armazenamento(),
): boolean {
  const vista = ler(store, LAST_VERSION_KEY)
  if (vista === null) return temPreferenciasGuardadas(store)
  return compararVersoes(vista, VERSAO_DA_MUDANCA) < 0
}

/** Mostra-se a nota? */
export function deveMostrarNota(
  store: Armazenamento | null = armazenamento(),
): boolean {
  return podeTerVistoFrisoAntigo(store) && !notaJaDispensada(store)
}

/** A decisão, tomada uma única vez por arranque.
 *
 *  Tem de ser assim porque a decisão e o registo da versão andam
 *  agarrados: decidir lê a versão anterior, registar apaga-a. Feitos
 *  juntos e uma só vez, a ordem deixa de poder correr mal, e deixa de
 *  importar quem chama primeiro (o friso não é montado no telemóvel, o
 *  arranque da aplicação é). */
let decisao: boolean | null = null

export function decidirERegistar(
  versao: string,
  store: Armazenamento | null = armazenamento(),
): boolean {
  if (decisao === null) {
    decisao = deveMostrarNota(store)
    registarVersaoVista(versao, store)
  }
  return decisao
}

/** Só para os testes: esquece a decisão deste arranque. */
export function esquecerDecisao(): void {
  decisao = null
}
